import { createRouter, createWebHistory } from "vue-router";
import store from "../store";
import NotFoundView from "../views/NotFoundView.vue";
import BacktestOverviewTab from "@/views/admin/backtests/OverviewTab.vue";
import BacktestChartTab from "@/views/admin/backtests/ChartTab.vue";
import BacktestDailyReturnsTab from "@/views/admin/backtests/DailyReturnsTab.vue";
import BacktestOrderFillsTab from "@/views/admin/backtests/OrderFillsTab.vue";
import BacktestReportTab from "@/views/admin/backtests/ReportTab.vue";
import BacktestOHLCExplorerTab from "@/views/admin/backtests/OHLCExplorerTab.vue";
import BacktestRangeExplorerTab from "@/views/admin/backtests/RangeExplorerTab.vue";
import BacktestSentimentExplorerTab from "@/views/admin/backtests/SentimentExplorerTab.vue";
import BacktestMACDExplorerTab from "@/views/admin/backtests/MACDExplorerTab.vue";
import BotLogTab from "@/views/admin/apikeys/BotLogTab.vue";

const routes = [
  {
    path: "/",
    name: "Root",
    beforeEnter: async (to, from, next) => {
      const isAuthenticated = await store.dispatch("auth/checkAuth");
      if (!isAuthenticated) {
        next("/login");
        return;
      }

      const isStaff = store.getters["auth/isStaff"];
      const isSuperAdmin = store.getters["auth/isSuperAdmin"];
      const isBrandAdmin = store.getters["auth/isBrandAdmin"];

      if (isSuperAdmin) {
        // Superadmins go to trading dashboard
        next("/admin/trading-dashboard");
      } else if (isStaff || isBrandAdmin) {
        // Staff users and brand admins/owners go to admin bots page
        next("/admin/bots");
      } else {
        next("/dashboard");
      }
    },
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/auth/LoginView.vue"),
  },
  {
    path: "/set-password/:uid/:token",
    name: "SetPassword",
    component: () => import("../views/auth/ChangePasswordView.vue"),
    props: true,
  },
  {
    path: "/reset-password/:uid/:token",
    name: "ResetPassword",
    component: () => import("../views/auth/ChangePasswordView.vue"),
    props: true,
  },
  {
    path: "/reset-password-request",
    name: "PasswordResetRequest",
    component: () => import("../views/auth/PasswordResetRequestView.vue"),
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: () =>
      import("../views/client/dashboard/ClientDashboardView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/account",
    name: "Account",
    component: () => import("../views/client/account/AccountView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/bots",
    name: "Bots",
    component: () => import("../views/client/bots/BotView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/bots/:botId/scaled-orders",
    name: "BotScaledOrders",
    component: () => import("../views/client/bots/BotScaledOrdersView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/reports",
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "Reports",
        component: () => import("../views/client/reports/ReportsLandingView.vue"),
      },
      {
        path: "waterfall",
        name: "ReportsWaterfall",
        component: () => import("../views/client/reports/WaterfallReportView.vue"),
      },
      {
        path: "transfers",
        name: "ReportsTransfers",
        component: () => import("../views/client/reports/TransferHistoryView.vue"),
      },
      {
        path: "generated",
        name: "GeneratedReports",
        component: () => import("../views/client/reports/ReportView.vue"),
      },
    ],
  },
  {
    path: "/borrowings",
    // page maps to the backend's PLATFORM_ONLY_PAGES entry so the guard can
    // allow platform-brand (QuantM Alpha) clients via allowed_admin_pages.
    meta: { requiresAuth: true, platformOnly: true, page: "borrowings" },
    children: [
      {
        path: "",
        name: "Borrowings",
        component: () => import("../views/client/borrowings/BorrowingsView.vue"),
      },
      {
        path: "new",
        name: "BorrowingsNew",
        component: () => import("../views/client/borrowings/BorrowPage.vue"),
      },
    ],
  },
  {
    path: "/admin",
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      {
        path: "",
        name: "AdminHome",
        beforeEnter: (to, from, next) => {
          // Redirect based on user type
          const isStaff = store.getters["auth/isStaff"];
          const isSuperAdmin = store.getters["auth/isSuperAdmin"];
          const isBrandAdmin = store.getters["auth/isBrandAdmin"];

          if (isSuperAdmin) {
            // Superadmins go to trading dashboard
            next("/admin/trading-dashboard");
          } else if (isStaff || isBrandAdmin) {
            // Staff users and brand admins/owners go to bots page
            next("/admin/bots");
          } else {
            next("/dashboard");
          }
        },
      },
      {
        path: "performance",
        name: "AdminPerformance",
        component: () =>
          import("../views/admin/dashboard/AdminDashboardView.vue"),
      },
      {
        path: "trading-dashboard",
        name: "AdminTradingDashboard",
        component: () =>
          import("../views/admin/dashboard/TradingDashboardView.vue"),
      },
      {
        path: "profit-sharing",
        name: "AdminProfitSharing",
        component: () => import("../views/admin/profit-sharing/ListView.vue"),
      },
      {
        path: "profit-sharing/report/:reportId",
        name: "ProfitShareReportDetail",
        component: () => import("../views/admin/profit-sharing/ReportDetailView.vue"),
      },
      {
        path: "accounts",
        name: "AdminAccounts",
        component: () => import("../views/admin/accounts/ListView.vue"),
      },
      {
        path: "accounts/:id",
        component: () => import("../views/admin/accounts/DetailView.vue"),
        children: [
          {
            path: "",
            name: "AccountOverview",
            component: () => import("../views/admin/accounts/OverviewTab.vue"),
          },
          {
            path: "bots",
            name: "AccountBots",
            component: () => import("../views/admin/accounts/BotsTab.vue"),
          },
          {
            path: "keys",
            name: "AccountAPIKeys",
            component: () => import("../views/admin/accounts/APIKeysTab.vue"),
          },
          {
            path: "transfers",
            name: "AccountTransfers",
            component: () => import("../views/admin/accounts/TransfersTab.vue"),
          },
          {
            path: "portal-logins",
            name: "AccountPortalLogins",
            component: () =>
              import("../views/admin/accounts/PortalLoginsTab.vue"),
          },
          {
            path: "user-access",
            name: "AccountUserAccess",
            component: () =>
              import("../views/admin/accounts/UserAccessTab.vue"),
          },
          {
            path: "equity",
            name: "AccountEquity",
            component: () => import("../views/admin/accounts/EquityTab.vue"),
          },
        ],
      },
      {
        path: "ins-loans",
        name: "AdminLoans",
        component: () => import("../views/admin/insloans/DetailView.vue"),
        meta: { platformOnly: true },
        children: [
          {
            path: "",
            name: "LoanOverview",
            component: () => import("../views/admin/insloans/OverviewTab.vue"),
          },
          {
            path: "uids",
            name: "LoanUids",
            component: () => import("../views/admin/insloans/UidsTab.vue"),
          },
          {
            path: "product-info",
            name: "LoanProductInfo",
            component: () =>
              import("../views/admin/insloans/ProductInfoTab.vue"),
          },
          {
            path: "loan-volume",
            name: "LoanVolume",
            component: () =>
              import("../views/admin/insloans/LoanVolumeTab.vue"),
          },
          {
            path: "loan-orders",
            name: "LoanOrders",
            component: () =>
              import("../views/admin/insloans/LoanOrdersTab.vue"),
          },
          {
            path: "repay-info",
            name: "LoanRepayInfo",
            component: () =>
              import("../views/admin/insloans/LoanRepayInfoTab.vue"),
          },
        ],
      },
      {
        path: "backtests",
        name: "Backtests",
        component: () => import("../views/admin/backtests/ListView.vue"),
        meta: { platformOnly: true },
      },
      {
        path: "agents",
        name: "AgentWorkspace",
        component: () => import("../views/admin/agents/WorkspaceView.vue"),
        meta: { platformOnly: true },
      },
      {
        path: "ohlc-explorer",
        name: "OHLCExplorer",
        component: BacktestOHLCExplorerTab,
      },
      {
        path: "range-explorer",
        name: "RangeExplorer",
        component: BacktestRangeExplorerTab,
      },
      {
        path: "sentiment-explorer",
        name: "SentimentExplorer",
        component: BacktestSentimentExplorerTab,
      },
      {
        path: "macd-explorer",
        name: "MACDExplorer",
        component: BacktestMACDExplorerTab,
      },
      {
        path: "backtests/:id",
        component: () => import("../views/admin/backtests/DetailView.vue"),
        children: [
          {
            path: "",
            name: "BacktestOverview",
            component: BacktestOverviewTab,
          },
          {
            path: "report",
            name: "BacktestReport",
            component: BacktestReportTab,
          },
          {
            path: "chart",
            name: "BacktestChart",
            component: BacktestChartTab,
          },
          {
            path: "daily-returns",
            name: "BacktestDailyReturns",
            component: BacktestDailyReturnsTab,
          },
          {
            path: "order-fills",
            name: "BacktestOrderFills",
            component: BacktestOrderFillsTab,
          },
          {
            path: "notes",
            name: "BacktestNotes",
            component: () => import("@/views/admin/notes/NotesTab.vue"),
            meta: {
              title: "Backtest Notes",
            },
          },
          {
            path: "ohlc-explorer",
            name: "BacktestOHLCExplorer",
            component: BacktestOHLCExplorerTab,
          },
          {
            path: "range-explorer",
            name: "BacktestRangeExplorer",
            component: BacktestRangeExplorerTab,
          },
          {
            path: "sentiment-explorer",
            name: "BacktestSentimentExplorer",
            component: BacktestSentimentExplorerTab,
          },
          {
            path: "macd-explorer",
            name: "BacktestMACDExplorer",
            component: BacktestMACDExplorerTab,
          },
        ],
      },
      {
        path: "tools",
        children: [
          {
            path: "",
            name: "AdminTools",
            component: () => import("../views/client/tools/ToolsLandingView.vue"),
          },
          {
            path: "scaled-orders",
            name: "AdminScaledOrders",
            component: () => import("../views/client/tools/ScaledOrderView.vue"),
          },
          {
            path: "scaled-orders-legacy",
            name: "AdminScaledOrdersLegacy",
            component: () => import("../views/client/tools/ScaledOrderViewLegacy.vue"),
          },
        ],
      },
      {
        path: "bots",
        name: "AdminBots",
        component: () => import("../views/admin/bots/ListView.vue"),
      },
      {
        path: "positions",
        name: "AdminPositions",
        component: () => import("../views/admin/positions/ListView.vue"),
        meta: { platformOnly: true },
        beforeEnter: (to, from, next) => {
          const isSuperAdmin = store.getters["auth/isSuperAdmin"];
          if (!isSuperAdmin) {
            next("/admin/bots");
          } else {
            next();
          }
        },
      },
      {
        path: "signals",
        component: () => import("../views/admin/signals/SignalsLayout.vue"),
        redirect: { name: "AdminSignalsCointegration" },
        children: [
          {
            path: "cointegration",
            name: "AdminSignalsCointegration",
            component: () => import("../views/admin/signals/CointegrationView.vue"),
          },
          {
            path: "pf-daily",
            name: "AdminSignalsPFDaily",
            component: () => import("../views/admin/signals/PFDailyView.vue"),
          },
          {
            path: "baskets",
            name: "AdminSignalsBaskets",
            component: () => import("../views/admin/signals/BasketsView.vue"),
          },
          {
            path: "24hshortscanner",
            name: "AdminSignals24hShortScanner",
            component: () => import("../views/admin/signals/ShortScannerView.vue"),
          },
          {
            path: "macd",
            name: "AdminSignalsMACD",
            component: () => import("../views/admin/signals/MACDView.vue"),
          },
          {
            path: "macd-signal-service",
            redirect: { name: "AdminSignalsMACD" },
          },
        ],
      },
      {
        path: "bots/pulse",
        name: "AdminBotsPulse",
        component: () => import("../views/admin/bots/ListView.vue"),
      },
      {
        path: "bots/:id",
        component: () => import("../views/admin/bots/DetailView.vue"),
        children: [
          {
            path: "",
            redirect: { name: "AdminBotPosition" },
          },
          {
            path: "position",
            name: "AdminBotPosition",
            component: () => import("../views/admin/bots/PositionTab.vue"),
          },
          {
            path: "state",
            name: "AdminBotState",
            component: () => import("../views/admin/bots/StateTab.vue"),
          },
          {
            path: "active-orders",
            name: "AdminBotActiveOrders",
            component: () => import("../views/admin/bots/ActiveOrders.vue"),
          },
          {
            path: "open-orders",
            name: "AdminBotOpenOrders",
            component: () => import("../views/admin/bots/OpenOrdersTab.vue"),
          },
          {
            path: "entry",
            name: "AdminBotEntry",
            component: () => import("../views/admin/bots/EntryTab.vue"),
          },
          {
            path: "entry.csv",
            name: "AdminBotEntryCSV",
            component: () => import("../views/admin/bots/EntryTab.vue"),
            beforeEnter: (to, from, next) => {
              // Store the request for CSV in query params
              // This will be detected by the component
              next({
                name: "AdminBotEntry",
                params: { id: to.params.id },
                query: { download: "csv" },
              });
            },
          },
          {
            path: "profit.csv",
            name: "AdminBotProfitCSV",
            component: () => import("../views/admin/bots/ProfitTab.vue"),
            beforeEnter: (to, from, next) => {
              // Store the request for CSV in query params
              next({
                name: "AdminBotProfit",
                params: { id: to.params.id },
                query: { download: "csv" },
              });
            },
          },
          {
            path: "profit",
            name: "AdminBotProfit",
            component: () => import("../views/admin/bots/ProfitTab.vue"),
          },
          {
            path: "position-history",
            name: "AdminBotPositionHistory",
            component: () => import("../views/admin/bots/PositionHistoryTab.vue"),
          },
          {
            path: "trades",
            name: "AdminBotTrades",
            component: () => import("../views/admin/bots/TradesTab.vue"),
          },
          {
            path: "cancelled",
            name: "AdminBotCancelled",
            component: () => import("../views/admin/bots/CancelledTab.vue"),
          },
          {
            path: "rejected",
            name: "AdminBotRejected",
            component: () => import("../views/admin/bots/RejectedTab.vue"),
          },
          {
            path: "funding-fees",
            name: "AdminBotFundingFees",
            component: () => import("../views/admin/bots/FundingFeesTab.vue"),
          },
          {
            path: "notes",
            name: "AdminBotNotes",
            component: () => import("@/views/admin/notes/NotesTab.vue"),
            meta: {
              title: "Bot Notes",
            },
          },
          {
            path: "audit-log",
            name: "AdminBotAuditLog",
            component: () => import("../views/admin/bots/AuditLogTab.vue"),
          },
          {
            path: "transfers",
            name: "AdminBotTransfers",
            component: () => import("../views/admin/bots/TransfersTab.vue"),
          },
          {
            path: "trading",
            name: "AdminBotTrading",
            component: () => import("../views/admin/bots/TradingTab.vue"),
          },
          {
            path: "returns",
            name: "AdminBotReturns",
            component: () => import("../views/admin/bots/ReturnsTab.vue"),
          },
          {
            path: "daily-returns",
            name: "AdminBotDailyReturns",
            component: () => import("../views/admin/bots/DailyReturnsTab.vue"),
          },
        ],
      },
      {
        path: "brand-members",
        name: "AdminBrandMembers",
        component: () => import("../views/admin/brand-members/ListView.vue"),
      },
      {
        path: "settings",
        component: () => import("../views/admin/settings/DetailView.vue"),
        // Land on Strategies by default — accessible to every role that passes
        // the beforeEnter gate below. Avoids a blank panel when the parent route
        // is hit directly (e.g. via the navbar Settings link).
        redirect: { name: "SettingsStrategies" },
        beforeEnter: (to, from, next) => {
          const isSuperAdmin = store.getters["auth/isSuperAdmin"];
          const isStaff = store.getters["auth/isStaff"];
          const isBrandAdmin = store.getters["auth/isBrandAdmin"];
          if (!isSuperAdmin && !isStaff && !isBrandAdmin) {
            // Redirect non-admin/non-staff users to the client account/settings page
            next("/account");
          } else {
            next();
          }
        },
        children: [
          {
            path: "global-settings",
            name: "SettingsGlobal",
            component: () =>
              import("../views/admin/settings/GlobalSettingsTab.vue"),
            meta: { platformOnly: true },
          },
          {
            path: "global-actions",
            name: "SettingsGlobalActions",
            component: () =>
              import("../views/admin/settings/GlobalActionsTab.vue"),
            meta: { platformOnly: true },
          },
          {
            path: "profile-settings",
            name: "SettingsProfile",
            component: () =>
              import("../views/admin/settings/ProfileSettingsTab.vue"),
          },
          {
            path: "strategies",
            name: "SettingsStrategies",
            component: () =>
              import("../views/admin/settings/StrategiesTab.vue"),
          },
          {
            path: "reporting-periods",
            name: "SettingsReportingPeriods",
            component: () =>
              import("../views/admin/settings/ReportingPeriodsTab.vue"),
          },
          {
            path: "account-tiers",
            name: "SettingsAccountTiers",
            component: () =>
              import("../views/admin/settings/AccountTiersTab.vue"),
          },
          {
            path: "access-tokens",
            name: "SettingsAccessTokens",
            component: () =>
              import("../views/admin/settings/BacktestingAPIKeysTab.vue"),
          },
        ],
      },
      {
        path: "keys/:id",
        component: () => import("../views/admin/apikeys/DetailView.vue"),
        children: [
          {
            path: "",
            name: "APIKeyOverview",
            component: () => import("../views/admin/apikeys/OverviewTab.vue"),
          },
          {
            path: "orders",
            name: "APIKeyOrders",
            component: () => import("../views/admin/apikeys/OrdersTab.vue"),
          },
          {
            path: "bulk-actions",
            name: "APIKeyBulkActions",
            component: () =>
              import("../views/admin/apikeys/BulkActionsTab.vue"),
          },
          {
            path: "equity",
            name: "APIKeyEquity",
            component: () => import("../views/admin/apikeys/EquityTab.vue"),
          },
          {
            path: "rate-limits",
            name: "APIKeyRateLimits",
            component: () =>
              import("../views/admin/apikeys/RateLimitsTab.vue"),
            meta: {
              requiresAuth: true,
              requiresAdmin: true,
            },
          },
          {
            path: "bot-log",
            name: "APIKeyBotLog",
            component: BotLogTab,
            meta: {
              requiresAuth: true,
              requiresAdmin: true,
            },
          },
          {
            path: "spot-margin",
            name: "APIKeyBorrowings",
            component: () => import("../views/admin/apikeys/SpotMarginTab.vue"),
            meta: {
              requiresAuth: true,
              requiresAdmin: true,
            },
          },
          {
            path: "transfers",
            name: "APIKeyTransfers",
            component: () => import("../views/admin/apikeys/TransfersTab.vue"),
            meta: {
              requiresAuth: true,
              requiresAdmin: true,
            },
          },
          {
            path: "position-history",
            name: "APIKeyPositionHistory",
            component: () =>
              import("../views/admin/apikeys/PositionHistoryTab.vue"),
            meta: {
              requiresAuth: true,
              requiresAdmin: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: NotFoundView,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    const isAuthenticated = await store.dispatch("auth/checkAuth");
    if (!isAuthenticated) {
      next("/login");
      return;
    }

    // Brand scoping — platformOnly routes are reserved for the QuantM Alpha
    // platform brand. Admin routes stay staff/superadmin-only. Client routes
    // (e.g. /borrowings) are open to any client on the platform brand: the
    // brand's allowed_admin_pages is brand-derived and only the platform
    // brand includes platform-only pages (backend brands/constants.py), so
    // external-brand users get redirected away while QuantM Alpha clients
    // pass through. Matches the navbar's canAccessBorrowings check.
    if (to.matched.some((record) => record.meta.platformOnly)) {
      const isSuperAdmin = store.getters["auth/isSuperAdmin"];
      const isStaff = store.getters["auth/isStaff"];
      if (!isSuperAdmin && !isStaff) {
        const isAdminRoute = to.matched.some((r) => r.meta.requiresAdmin);
        const page = to.matched.find((r) => r.meta.platformOnly)?.meta.page;
        const brandPages = store.getters["brand/allowedAdminPages"];
        const brandAllowsPage = !isAdminRoute && page && brandPages.includes(page);
        if (!brandAllowsPage) {
          next(isAdminRoute ? "/admin/bots" : "/dashboard");
          return;
        }
      }
    }

    if (to.matched.some((record) => record.meta.requiresAdmin)) {
      const isStaff = store.getters["auth/isStaff"];
      const hasGrantedAccess = store.getters["auth/hasGrantedAccess"];
      const isBrandAdmin = store.getters["auth/isBrandAdmin"];

      if (!isStaff && !hasGrantedAccess && !isBrandAdmin) {
        next("/dashboard");
      } else if (hasGrantedAccess && !isStaff && !isBrandAdmin) {
        // Non-staff non-brand-admin users with granted access can only
        // access certain pages. Brand admins/owners fall through to full
        // access even though they also have UserAccountAccess grants
        // (create_brand.py adds both).
        const allowedRoutes = [
          // Bot list views
          'AdminBots', 'AdminBotsPulse',
          // Bot detail views
          'AdminBotPosition', 'AdminBotActiveOrders', 'AdminBotOpenOrders',
          'AdminBotEntry', 'AdminBotEntryCSV', 'AdminBotProfit', 'AdminBotProfitCSV',
          'AdminBotPositionHistory', 'AdminBotTrades', 'AdminBotCancelled', 'AdminBotRejected',
          'AdminBotFundingFees', 'AdminBotNotes', 'AdminBotAuditLog', 'AdminBotTransfers',
          'AdminBotTrading', 'AdminBotReturns', 'AdminBotDailyReturns', 'AdminBotState',
          // Account views
          'AdminAccounts', 'AccountOverview', 'AccountBots', 'AccountAPIKeys',
          'AccountTransfers', 'AccountPortalLogins', 'AccountEquity', 'AccountUserAccess'
        ];

        if (allowedRoutes.includes(to.name) || to.path === '/admin' || to.path === '/admin/') {
          next();
        } else {
          // Redirect to accounts page if trying to access unauthorized route
          next("/admin/accounts");
        }
      } else {
        next();
      }
    } else {
      next();
    }
  } else {
    next();
  }
});

export default router;
